import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { TableExportDirective } from 'src/app/shared/table-export/table-export.directive';
import moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Subscription, forkJoin, of } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { catchError, map } from 'rxjs/operators';
import { BudgetCalculationService } from 'src/app/budget/budget-calculation.service';
import { FetchEmployeesWithBudgets } from 'src/app/budget/budget.actions';
import { BudgetState } from 'src/app/budget/budget.state';
import { ComanageService } from 'src/app/comanage/comanage.service';
import { CompanyState } from 'src/app/companies/company.state';
import { FetchEmployees } from 'src/app/employees/employees.actions';
import { EmployeeState } from 'src/app/employees/employees.state';
import { FinancialService } from 'src/app/financial/financial.service';
import ChildInvoiceLink from 'src/app/models/ChildInvoiceLink';
import { Employee } from 'src/app/models/Employee';
import { LinkedFileToObject } from 'src/app/models/LinkedFileToObject';
import { LinkedObjectsToFileWithId } from 'src/app/models/LinkedObjectsToFileWithId';
import Project, { ContractBudgetStatus } from 'src/app/models/Project';
import { PartnerDetailsModalComponent } from 'src/app/partners/partner-details-modal/partner-details-modal.component';
import { GlobalFilesService } from 'src/app/shared/global-files/global-files.service';
import { JiraLoginService } from 'src/app/shared/login-components/jira-login/jira-login.service';
import { TimesheetsService } from 'src/app/timesheets/timesheets.service';

import { JiraMissingProjectsModalComponent } from '../jira-missing-projects-modal/jira-missing-projects-modal.component';
import { ProjectDeleteModalComponent } from '../project-delete-modal/project-delete-modal.component';
import { ProjectDetailModalComponent } from '../project-detail-modal/project-detail-modal.component';
import { ProjectInfoModalComponent } from '../project-info-modal/project-info-modal.component';
import { FetchProjectsForOverview, UpdateFinishedOfProject } from '../project.actions';
import { ProjectService } from '../project.service';
import { ProjectState } from '../project.state';
import { openExternalUrl } from '../../shared/safe-url';

@Component({
    selector: 'app-project-list',
    templateUrl: './project-list.component.html',
    styleUrls: ['./project-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ProjectListComponent implements OnInit, OnDestroy {
    @Select(CompanyState.companyUuid) companyUuid$: Observable<string>;
    @Select(ProjectState.projects) projects$: Observable<Project[]>;
    @Select(EmployeeState.Employees) employees$: Observable<Employee[]>;
    @Input() type: ProjectListType;
    @Output() infoModalClosed: EventEmitter<any> = new EventEmitter();
    companyUuid: string;
    filter = '';
    filename: string;
    projectPdf: any;
    displayedColumns: string[] = [
        'eye',
        'title',
        'endDateWorklogs',
        'endDateInvoice',
        'warranty',
        'maintenanceContract',
        'researchDevelopment',
        'comanageContact',
        'businessDeveloper',
        'projectType',
        'jiraLink',
        'creatorId',
        'edit',
    ];
    dataSource: MatTableDataSource<Project>;
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(TableExportDirective, { static: true }) exporter: TableExportDirective;
    private subscriptions = new Subscription();
    showOnlyMaintenanceContracts: boolean = false;
    maintenanceFilterBtnText: string;
    allProjectsForJiraCheck: Project[] = [];
    jiraProjects: Array<{ projectName?: string; projectKey?: string; projectId?: string | number }> = [];
    jiraMissingProjects: Array<{ projectName: string; projectKey: string; projectId?: string | number }> = [];
    jiraProjectsLoaded = false;
    private jiraKeyByProjectId: Map<string, string> = new Map<string, string>();
    coManageContactLabelById: Map<string, string> = new Map<string, string>();
    currentYear = new Date().getFullYear();
    private financialTabCostByProjectId: Map<string, number> = new Map<string, number>();
    private accountBudgetStatusesByProjectId: Map<string, ContractBudgetStatus[]> = new Map<string, ContractBudgetStatus[]>();
    private accountBudgetStatusesLoadingByProjectId: Map<string, boolean> = new Map<string, boolean>();
    private accountBudgetStatusesLoadedProjectIds: Set<string> = new Set<string>();
    private contractFallbackStatusesByProjectId: Map<string, ContractBudgetStatus[]> = new Map<string, ContractBudgetStatus[]>();
    private contractFallbackLoadedProjectIds: Set<string> = new Set<string>();
    private incomingInvoicesAmountByProjectId: Map<string, number> = new Map<string, number>();
    private outgoingInvoicesAmountByProjectId: Map<string, number> = new Map<string, number>();
    private invoiceBudgetByProjectId: Map<string, number> = new Map<string, number>();
    private employeesById: Record<string, Employee> = {};

    constructor(
        private modalService: NgbModal,
        private store: Store,
        public activeModal: NgbActiveModal,
        private router: Router,
        private toastr: ToastrService,
        private projectService: ProjectService,
        private sanitizer: DomSanitizer,
        private logger: NGXLogger,
        private jiraLoginService: JiraLoginService,
        private comanageService: ComanageService,
        private financialService: FinancialService,
        private globalFilesService: GlobalFilesService,
        private budgetCalc: BudgetCalculationService,
        private timesheetsService: TimesheetsService,
    ) {}

    ngOnInit(): void {
        this.subscriptions.add(this.companyUuid$.subscribe((uuid) => (this.companyUuid = uuid)));
        this.subscriptions.add(
            this.employees$.subscribe((employees) => {
                if (!employees) {
                    this.store.dispatch(new FetchEmployees());
                    return;
                }

                this.employeesById = (employees || []).reduce((acc: Record<string, Employee>, employee: Employee) => {
                    if (employee?.uuid) {
                        acc[employee.uuid] = employee;
                    }
                    return acc;
                }, {});
                this.triggerTableRefresh();
            }),
        );
        this.loadCoManageContactLabels();

        this.subscriptions.add(
            this.projects$.subscribe((projects) => {
                if (!projects) {
                    this.logger.debug('Fetching user');
                    this.store.dispatch(new FetchProjectsForOverview());
                    return;
                }

                const filtered: Project[] = projects.filter((project) => {
                    if (this.type === ProjectListType.ONGOING) return !project.finished && !project.archived;
                    else if (this.type === ProjectListType.ARCHIVED) return project.archived;
                    else if (this.type === ProjectListType.FINISHED) return project.finished;
                    else if (this.type === ProjectListType.HAS_MAINTENANCE_CONTRACT) return project.maintenanceContract;
                    return true;
                });

                this.allProjectsForJiraCheck = projects || [];
                this.recomputeMissingJiraProjects();

                this.dataSource = new MatTableDataSource(filtered);
                this.dataSource.sort = this.sort;

                // Do not preload project-account details on overview list.
                // Account details are fetched in project detail/account tabs.
                // Do not fetch project detail per row on overview.
                // Contract fallback details are resolved in project detail contexts.

                // Sorts project list alphabetically, independant of project.title casing
                this.dataSource.sortingDataAccessor = (data: any, sortHeaderId: string): string => {
                    if (sortHeaderId === 'businessDeveloper') {
                        return this.getBusinessDeveloperDisplay(data).toLocaleLowerCase();
                    }

                    if (sortHeaderId === 'researchDevelopment') {
                        return data.researchDevelopmentEligible ? '1' : '0';
                    }

                    if (typeof data[sortHeaderId] === 'string') {
                        return data[sortHeaderId].toLocaleLowerCase();
                    }

                    return data[sortHeaderId];
                };

                this.applyFilter(this.filter);
            }),
        );

        this.subscriptions.add(
            this.jiraLoginService.getJiraProjectKeys().subscribe(
                (jiraProjects: any[]) => {
                    this.jiraProjects = (jiraProjects || []).map((project) => ({
                        projectName: project?.projectName,
                        projectKey: project?.projectKey,
                        projectId: project?.projectId,
                    }));
                    this.buildJiraKeyLookupMap();
                    this.jiraProjectsLoaded = true;
                    this.recomputeMissingJiraProjects();
                },
                () => {
                    this.jiraProjectsLoaded = true;
                    this.jiraProjects = [];
                    this.jiraMissingProjects = [];
                    this.jiraKeyByProjectId.clear();
                },
            ),
        );

        this.maintenanceFilterBtnText = 'Show projects with maintenance only';
    }

    applyFilter(filterValue) {
        this.dataSource.filter = filterValue.trim().toLowerCase();
    }

    ngAfterViewInit() {
        this.dataSource.sort = this.sort;
    }

    openAddProjectModal() {
        this.logger.debug('Opening projectDetailModal');
        const modalRef = this.modalService.open(ProjectDetailModalComponent, {
            windowClass: 'modal-pane',
            animation: false,
        });
        modalRef.componentInstance.inOverview = true;
    }

    openProjectUpdateModal(project: Project) {
        this.logger.debug('Opening projectDetailModal - update');
        const modalRef = this.modalService.open(ProjectDetailModalComponent, {
            windowClass: 'modal-pane',
            animation: false,
        });
        modalRef.componentInstance.project = project;
        modalRef.componentInstance.isUpdate = true;
    }

    openProjectDeleteModal(project: Project) {
        this.logger.debug('Opening projectDeleteModal');
        const modalRef = this.modalService.open(ProjectDeleteModalComponent, { windowClass: 'modal-prompt', animation: false });
        modalRef.componentInstance.project = project;
    }

    openProjectInfoModal(projectUuid: string) {
        this.logger.debug('Opening projectInfoModal');
        const modalRef = this.modalService.open(ProjectInfoModalComponent, {
            windowClass: 'modal-huge',
            animation: false,
        });
        modalRef.componentInstance.projectUuid = projectUuid;
    }

    openPartnerDetailModalComponent(partnerUuid: string) {
        this.logger.debug('Opening partnerDetailModal');
        const modalRef = this.modalService.open(PartnerDetailsModalComponent, { windowClass: 'modal-prompt', animation: false });
        modalRef.componentInstance.partnerUuid = partnerUuid;
    }

    projectDetail(project: Project) {
        this.logger.debug('Navigating to projectdetail');
        this.router.navigate(['/', 'works', project.uuid, 'detail']);
    }

    toggleFinishedProject(project: Project) {
        this.logger.debug('Attempt to update finished status of project');
        this.subscriptions.add(
            this.store.dispatch(new UpdateFinishedOfProject(project.uuid)).subscribe(
                () => {
                    this.logger.debug('Successfully updated finished status of project');
                    this.toastr.success('Project status updated');
                },
                (error) => {
                    this.logger.debug('Failed to update finished status of project');
                    this.toastr.error('Something went wrong');
                },
            ),
        );
    }

    checkDates(project: Project): boolean {
        if (project.endDate) {
            const endDate = moment(project.endDate, 'yyyy-MM-DD hh:mm:ss');

            if (endDate < moment()) {
                return true;
            }
        }
        return false;
    }

    getProjectDateRangeLabel(project: Project): string {
        const startDateLabel = this.formatProjectDate(project?.startDate);
        const endDateLabel = this.formatProjectDate(project?.endDate);
        return `${startDateLabel} - ${endDateLabel}`;
    }

    private formatProjectDate(dateValue?: string): string {
        if (!dateValue) {
            return '-';
        }

        const parsedDate = moment(dateValue);
        if (!parsedDate.isValid()) {
            return '-';
        }

        return parsedDate.format('DD/MM/YYYY');
    }

    hasWarranty(project: Project): boolean {
        if (project.warranty) {
            return true;
        }
        return false;
    }

    hasMaintenanceContract(project: Project): boolean {
        if (project.maintenanceContract) {
            return true;
        }
        return false;
    }

    isResearchDevelopmentEligible(project: Project): boolean {
        return !!project?.researchDevelopmentEligible;
    }

    showContractBudgetProgress(project: Project): boolean {
        return this.getOverviewBudgetStatuses(project).length > 0;
    }

    showInvoiceBudgetProgress(project: Project): boolean {
        const hasContractBudget = this.getResolvedContractBudget(project) > 0;
        const hasInvoiceAmount = this.getBudgetByInvoiceAmount(project) > 0;
        return hasContractBudget || hasInvoiceAmount;
    }

    getCostVsBudgetUsagePercentage(project: Project): number {
        const totalCost = this.getResolvedProjectTotalCostAmount(project);
        const budget = Number(project?.currentYearContractBudget ?? 0);
        if (Number.isFinite(totalCost) && totalCost >= 0 && Number.isFinite(budget) && budget > 0) {
            return (totalCost / budget) * 100;
        }

        const value = Number(project?.currentYearCostBudgetUsagePercentage ?? project?.currentYearBudgetUsagePercentage ?? 0);
        if (Number.isNaN(value) || !Number.isFinite(value) || value < 0) {
            return 0;
        }
        return value;
    }

    getResolvedProjectTotalCostAmount(project: Project): number {
        const projectId = project?.uuid;
        if (projectId && this.financialTabCostByProjectId.has(projectId)) {
            return Number(this.financialTabCostByProjectId.get(projectId) ?? 0);
        }

        const fallback = Number(project?.currentYearTotalCostAmount ?? project?.currentYearInvoicedAmount ?? 0);
        if (Number.isNaN(fallback) || !Number.isFinite(fallback) || fallback < 0) {
            return 0;
        }
        return fallback;
    }

    getContractBudgetYearUsagePercentage(project: Project): number {
        const amount = this.getResolvedContractSpentAmount(project);
        const budget = this.getResolvedContractBudget(project);
        if (Number.isFinite(amount) && amount >= 0 && Number.isFinite(budget) && budget > 0) {
            return (amount / budget) * 100;
        }

        const value = Number(project?.currentYearContractHoursBudgetUsagePercentage ?? 0);
        if (Number.isNaN(value) || !Number.isFinite(value) || value < 0) {
            return 0;
        }
        return value;
    }

    getCostVsBudgetProgressBarValue(project: Project): number {
        const usage = this.getCostVsBudgetUsagePercentage(project);
        return Math.max(0, Math.min(100, usage));
    }

    getCostVsBudgetTooltip(project: Project): string {
        const totalCost = this.getResolvedProjectTotalCostAmount(project);
        const budget = Number(project?.currentYearContractBudget ?? 0);
        const usage = this.getCostVsBudgetUsagePercentage(project);

        return [
            `Berekening: (totale kost / contractbudget) x 100`,
            `Totale kost: € ${totalCost.toFixed(2)}`,
            `Contractbudget: € ${Number.isFinite(budget) ? budget.toFixed(2) : '0.00'}`,
            `Resultaat: ${usage.toFixed(2)}%`,
            `Bron kost: linked invoices + timesheets (budgetlogica)`,
        ].join('\n');
    }

    getContractBudgetYearProgressBarValue(project: Project): number {
        const usage = this.getContractBudgetYearUsagePercentage(project);
        return Math.max(0, Math.min(100, usage));
    }

    getContractBudgetYearTooltip(project: Project): string {
        const amount = this.getResolvedContractSpentAmount(project);
        const budget = this.getResolvedContractBudget(project);
        const usage = this.getContractBudgetYearUsagePercentage(project);

        return [
            `Berekening: (contract hours kost / contractbudget) x 100`,
            `Contract hours kost: € ${amount.toFixed(2)}`,
            `Contractbudget: € ${Number.isFinite(budget) ? budget.toFixed(2) : '0.00'}`,
            `Resultaat: ${usage.toFixed(2)}%`,
            `Bron: actieve contractregels in huidig jaar`,
        ].join('\n');
    }

    getResolvedContractBudget(project: Project): number {
        const directBudget = Number(project?.currentYearContractBudget ?? 0);
        if (Number.isFinite(directBudget) && directBudget > 0) {
            return directBudget;
        }

        const projectId = project?.uuid;
        const accountBudget = ((projectId && this.accountBudgetStatusesByProjectId.get(projectId)) || []).reduce((sum, status) => {
            const budget = Number(status?.budget ?? 0);
            return sum + (Number.isFinite(budget) && budget > 0 ? budget : 0);
        }, 0);
        if (accountBudget > 0) {
            return accountBudget;
        }

        const fallbackBudget = ((projectId && this.contractFallbackStatusesByProjectId.get(projectId)) || []).reduce((sum, status) => {
            const budget = Number(status?.budget ?? 0);
            return sum + (Number.isFinite(budget) && budget > 0 ? budget : 0);
        }, 0);
        if (fallbackBudget > 0) {
            return fallbackBudget;
        }

        const statusesBudget = this.getContractBudgetStatuses(project).reduce((sum, status) => {
            const budget = Number(status?.budget ?? 0);
            return sum + (Number.isFinite(budget) && budget > 0 ? budget : 0);
        }, 0);
        if (statusesBudget > 0) {
            return statusesBudget;
        }

        return (project?.defaultLinkedEmployees || []).reduce((sum, link) => {
            const budget = Number(link?.budget ?? 0);
            return sum + (Number.isFinite(budget) && budget > 0 ? budget : 0);
        }, 0);
    }

    getResolvedContractSpentAmount(project: Project): number {
        const directAmount = Number(project?.currentYearContractHoursCostAmount ?? 0);
        if (Number.isFinite(directAmount) && directAmount > 0) {
            return directAmount;
        }

        const projectId = project?.uuid;
        const accountAmount = ((projectId && this.accountBudgetStatusesByProjectId.get(projectId)) || []).reduce((sum, status) => {
            const amount = Number(status?.amount ?? 0);
            return sum + (Number.isFinite(amount) && amount >= 0 ? amount : 0);
        }, 0);
        if (accountAmount > 0) {
            return accountAmount;
        }

        const fallbackAmount = ((projectId && this.contractFallbackStatusesByProjectId.get(projectId)) || []).reduce((sum, status) => {
            const amount = Number(status?.amount ?? 0);
            return sum + (Number.isFinite(amount) && amount >= 0 ? amount : 0);
        }, 0);
        if (fallbackAmount > 0) {
            return fallbackAmount;
        }

        return this.getContractBudgetStatuses(project).reduce((sum, status) => {
            const amount = Number(status?.amount ?? 0);
            return sum + (Number.isFinite(amount) && amount >= 0 ? amount : 0);
        }, 0);
    }

    private refreshContractFallbackStatusesForProjects(projects: Project[]): void {
        if (!projects?.length) {
            return;
        }

        projects
            .filter((project) => !!project?.uuid)
            .forEach((project) => {
                const projectId = project.uuid;
                if (!this.needsContractFallbackLoad(project)) {
                    return;
                }

                this.contractFallbackLoadedProjectIds.add(projectId);
                this.subscriptions.add(
                    this.projectService
                        .getProjectDetail(projectId)
                        .pipe(catchError(() => of(null)))
                        .subscribe((detailProject: Project) => {
                            const links = (detailProject?.defaultLinkedEmployees || []).filter((link) => Number(link?.budget || 0) > 0);
                            if (!links.length) {
                                this.contractFallbackStatusesByProjectId.set(projectId, []);
                                this.triggerTableRefresh();
                                return;
                            }

                            this.subscriptions.add(
                                this.timesheetsService
                                    .getTimeSheetsForProject(projectId)
                                    .pipe(catchError(() => of([])))
                                    .subscribe((timesheets: any[]) => {
                                        const invoiceByDay = !!detailProject?.invoiceByDay;
                                        const hoursPerDay = this.getProjectHoursPerDay(detailProject);
                                        const statuses: ContractBudgetStatus[] = links.map((link) => {
                                            const isFreelancer = this.isFreelancerEmployee(link?.employeeId);
                                            const workedHours = this.calculateWorkedHoursInContractPeriod(
                                                timesheets || [],
                                                projectId,
                                                link?.employeeId,
                                                link?.effectiveFrom,
                                                link?.effectiveTo,
                                            );
                                            const price = Number(link?.pricePerHour || 0);
                                            const budget = Number(link?.budget || 0);
                                            const amount = invoiceByDay
                                                ? (workedHours / hoursPerDay) * (Number.isFinite(price) && price > 0 ? price : 0)
                                                : workedHours * (Number.isFinite(price) && price > 0 ? price : 0);
                                            const totalAmount = amount;
                                            const usagePercentage = budget > 0 ? (totalAmount / budget) * 100 : 0;

                                            return {
                                                id: link?.uuid || `${projectId}-${link?.employeeId || 'contract'}`,
                                                sourceType: 'CONTRACT',
                                                label: link?.employeeId ? `Contract (${link.employeeId})` : 'Contract',
                                                budget,
                                                amount: totalAmount,
                                                usagePercentage: Number.isFinite(usagePercentage) && usagePercentage >= 0 ? usagePercentage : 0,
                                                freelancer: isFreelancer,
                                            } as ContractBudgetStatus;
                                        });

                                        this.contractFallbackStatusesByProjectId.set(projectId, statuses);
                                        this.triggerTableRefresh();
                                    }),
                            );
                        }),
                );
            });
    }

    private needsContractFallbackLoad(project: Project): boolean {
        const projectId = project?.uuid;
        if (!projectId) {
            return false;
        }

        if (this.contractFallbackLoadedProjectIds.has(projectId)) {
            return false;
        }

        const hasAccountStatuses = (this.accountBudgetStatusesByProjectId.get(projectId)?.length || 0) > 0;
        const hasContractStatuses = this.getContractBudgetStatuses(project).length > 0;
        const hasDirectBudget = Number(project?.currentYearContractBudget ?? 0) > 0;
        const hasDirectAmount = Number(project?.currentYearContractHoursCostAmount ?? 0) > 0;

        return !hasAccountStatuses && !hasContractStatuses && !hasDirectBudget && !hasDirectAmount;
    }

    private calculateWorkedHoursInContractPeriod(
        timesheets: any[],
        projectId: string,
        employeeId: string,
        effectiveFrom?: string,
        effectiveTo?: string,
    ): number {
        if (!employeeId || !projectId) {
            return 0;
        }

        const fromDate = this.parseOverviewIsoDate(effectiveFrom);
        const toDate = this.parseOverviewIsoDate(effectiveTo);

        return (timesheets || []).reduce((totalHours: number, timesheet: any) => {
            if (timesheet?.employee?.uuid !== employeeId) {
                return totalHours;
            }

            const timesheetDate = this.parseOverviewDateValue(timesheet?.expirationDate);
            if (!timesheetDate) {
                return totalHours;
            }
            if (fromDate && timesheetDate < fromDate) {
                return totalHours;
            }
            if (toDate && timesheetDate > toDate) {
                return totalHours;
            }

            const prorationFactor = this.getTimesheetProrationFactor(timesheetDate, fromDate, toDate);
            if (prorationFactor <= 0) {
                return totalHours;
            }

            const minutesInProject = (timesheet?.timeslots || []).reduce((minutes: number, slot: any) => {
                if (slot?.project?.uuid !== projectId) {
                    return minutes;
                }
                const value = Number(slot?.minutes || 0);
                return minutes + (Number.isFinite(value) && value > 0 ? value : 0);
            }, 0);

            return totalHours + (minutesInProject * prorationFactor) / 60;
        }, 0);
    }

    private getTimesheetProrationFactor(timesheetDate: Date, fromDate: Date | null, toDate: Date | null): number {
        const monthStart = new Date(timesheetDate.getFullYear(), timesheetDate.getMonth(), 1);
        const monthEnd = new Date(timesheetDate.getFullYear(), timesheetDate.getMonth() + 1, 0);

        const effectiveStart = fromDate && fromDate > monthStart ? fromDate : monthStart;
        const effectiveEnd = toDate && toDate < monthEnd ? toDate : monthEnd;

        if (effectiveEnd < effectiveStart) {
            return 0;
        }

        const totalDaysInMonth = monthEnd.getDate();
        if (totalDaysInMonth <= 0) {
            return 0;
        }

        const overlapDays = effectiveEnd.getDate() - effectiveStart.getDate() + 1;
        if (overlapDays <= 0) {
            return 0;
        }

        return Math.min(1, overlapDays / totalDaysInMonth);
    }

    private parseOverviewDateValue(value: string | Date | null | undefined): Date | null {
        if (!value) {
            return null;
        }

        if (value instanceof Date) {
            if (Number.isNaN(value.getTime())) {
                return null;
            }
            return new Date(value.getFullYear(), value.getMonth(), value.getDate());
        }

        const raw = String(value).trim();
        const isoPart = raw.split('T')[0];
        const parts = isoPart.split('-').map((part) => Number(part));
        if (parts.length === 3 && parts.every((part) => Number.isFinite(part) && !Number.isNaN(part))) {
            return new Date(parts[0], parts[1] - 1, parts[2]);
        }

        const parsed = new Date(raw);
        if (Number.isNaN(parsed.getTime())) {
            return null;
        }

        return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }

    private getProjectHoursPerDay(project: Project): number {
        const value = Number(project?.hoursPerDay);
        if (!Number.isFinite(value) || value <= 0) {
            return 8;
        }
        return value;
    }

    private triggerTableRefresh(): void {
        if (this.dataSource?.data?.length) {
            this.dataSource.data = [...this.dataSource.data];
        }
    }

    getContractBudgetStatuses(project: Project): ContractBudgetStatus[] {
        return (project?.currentYearContractBudgetStatuses || []).filter((status) => {
            const budget = Number(status?.budget ?? 0);
            return Number.isFinite(budget) && budget > 0;
        });
    }

    getOverviewBudgetStatuses(project: Project): ContractBudgetStatus[] {
        const projectId = project?.uuid;
        return ((projectId && this.accountBudgetStatusesByProjectId.get(projectId)) || []).filter((status) => {
            const budget = Number(status?.budget ?? 0);
            return Number.isFinite(budget) && budget > 0;
        });
    }

    isOverviewBudgetLoading(project: Project): boolean {
        const projectId = project?.uuid;
        return !!(projectId && this.accountBudgetStatusesLoadingByProjectId.get(projectId));
    }

    loadOverviewWorklogs(project: Project, event?: Event): void {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        const projectId = project?.uuid;
        if (!projectId) {
            return;
        }

        this.accountBudgetStatusesLoadedProjectIds.delete(projectId);
        this.accountBudgetStatusesLoadingByProjectId.delete(projectId);
        this.accountBudgetStatusesByProjectId.delete(projectId);
        this.triggerTableRefresh();
        this.refreshAccountBudgetStatusesForProjects([project]);
    }

    getContractBudgetStatusUsagePercentage(status: ContractBudgetStatus): number {
        const value = Number(status?.usagePercentage ?? 0);
        if (Number.isNaN(value) || !Number.isFinite(value) || value < 0) {
            return 0;
        }
        return value;
    }

    getContractBudgetStatusProgressBarValue(status: ContractBudgetStatus): number {
        const usage = this.getContractBudgetStatusUsagePercentage(status);
        return Math.max(0, Math.min(100, usage));
    }

    getContractBudgetStatusTooltip(status: ContractBudgetStatus): string {
        const label = status?.label || 'Contract/account';
        const amount = Number(status?.amount ?? 0);
        const budget = Number(status?.budget ?? 0);
        const totalHours = Number(status?.totalHours ?? 0);
        const contractPrice = Number(status?.contractPricePerHour ?? 0);
        const usage = this.getContractBudgetStatusUsagePercentage(status);
        const effectiveRate = totalHours > 0 && budget > 0 ? budget / totalHours : 0;

        const lines = [
            `Type: ${label}`,
            `Berekening: (actuele kost / budget) x 100`,
            `Actuele kost: € ${amount.toFixed(2)}`,
            `Budget: € ${budget.toFixed(2)}`,
            `Resultaat: ${usage.toFixed(2)}%`,
        ];

        if (totalHours > 0) {
            lines.push(`Gelogde uren: ${totalHours.toFixed(1)} uur`);
            if (contractPrice > 0) {
                lines.push(`Contractueel uurtarief: € ${contractPrice.toFixed(2)}`);
            }
            lines.push(`Effectief uurtarief: € ${effectiveRate.toFixed(2)}`);
        }

        if (status?.freelancer) {
            lines.push('Freelancer contract: incoming facturen worden niet meegerekend.');
        }

        return lines.join('\n');
    }

    getCostVsBudgetStatusUsagePercentage(project: Project, status: ContractBudgetStatus): number {
        const totalCost = this.getResolvedProjectTotalCostAmount(project);
        const budget = Number(status?.budget ?? 0);
        if (!Number.isFinite(totalCost) || totalCost < 0 || !Number.isFinite(budget) || budget <= 0) {
            return 0;
        }
        return (totalCost / budget) * 100;
    }

    getCostVsBudgetStatusProgressBarValue(project: Project, status: ContractBudgetStatus): number {
        const usage = this.getCostVsBudgetStatusUsagePercentage(project, status);
        return Math.max(0, Math.min(100, usage));
    }

    hasActiveWarrantyDates(project: Project): boolean {
        if (project.warrantyEndDate) {
            const warrantyEndDate = moment(project.warrantyEndDate, 'yyyy-MM-DD hh:mm:ss');

            if (warrantyEndDate.endOf('day').isBefore(moment().endOf('day'))) {
                return false;
            }
        }

        return true;
    }

    calculateRemainingWarrantyDays(project: Project): number {
        const today = moment().startOf('day');
        const warrantyEndDate = moment(project.warrantyEndDate);
        const daysRemaining = warrantyEndDate.diff(today, 'days');
        return daysRemaining;
    }

    downloadProjectPdf(project) {
        this.subscriptions.add(
            this.projectService.downloadProjectPdf(project.uuid).subscribe((pdf) => {
                this.filename = `${project.title}.pdf`;
                this.base64ToBlob(pdf.pdf);
            }),
        );
    }

    // Opens the permanent public customer-portal link for this company in a new tab
    // and copies it to the clipboard. The link is the same for every project of the company.
    openCustomerPortal() {
        this.subscriptions.add(
            this.projectService.getPortalLink().subscribe({
                next: (result) => {
                    if (!result || !result.url) {
                        this.toastr.error('Kon de dashboardlink niet ophalen.');
                        return;
                    }
                    openExternalUrl(result.url);
                    if (navigator.clipboard) {
                        navigator.clipboard
                            .writeText(result.url)
                            .then(() => this.toastr.success('Dashboardlink gekopieerd naar klembord.'))
                            .catch(() => undefined);
                    }
                },
                error: () => this.toastr.error('Kon de dashboardlink niet ophalen.'),
            }),
        );
    }

    base64ToBlob(data) {
        const byteChars = atob(data);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
            byteNumbers[i] = byteChars.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        this.projectPdf = this.sanitizer.bypassSecurityTrustUrl(url);
        this.logger.debug('Opening pdf in new window');
        openExternalUrl(url);
    }

    openMissingJiraProjectsPopup() {
        const modalRef = this.modalService.open(JiraMissingProjectsModalComponent, {
            windowClass: 'modal-pane',
            animation: false,
            size: 'lg',
        });
        modalRef.componentInstance.missingProjects = [...this.jiraMissingProjects];
        modalRef.componentInstance.jiraProjects = [...this.jiraProjects];
        modalRef.componentInstance.companyUuid = this.companyUuid;
        modalRef.componentInstance.refreshRequested.subscribe(() => {
            this.store.dispatch(new FetchProjectsForOverview());
        });
    }

    private recomputeMissingJiraProjects(): void {
        if (!this.jiraProjectsLoaded || !Array.isArray(this.jiraProjects) || this.jiraProjects.length === 0) {
            this.jiraMissingProjects = [];
            return;
        }

        // Build lookup maps for Jira projects from API
        const jiraKeyById = new Map<string, string>(); // projectId -> projectKey
        const jiraIdByKey = new Map<string, string>(); // projectKey -> projectId
        this.jiraProjects.forEach((project) => {
            const id = this.normalizeString(project?.projectId);
            const key = this.normalizeJiraKey(project?.projectKey);
            if (id && key) {
                jiraKeyById.set(id, key);
                jiraIdByKey.set(key, id);
            }
        });

        // Track which Jira projects are already linked
        const linkedJiraIds = new Set<string>(); // Jira projectId's that are linked
        const linkedJiraKeys = new Set<string>(); // Jira projectKey's that are linked
        const existingTitles = new Set<string>();

        (this.allProjectsForJiraCheck || []).forEach((project) => {
            // Track title for fallback matching
            const title = this.normalizeProjectTitle((project as any)?.title);
            if (title) {
                existingTitles.add(title);
            }

            // Check jiraProjectId (numeric ID stored in the project)
            const jiraProjectId = this.normalizeString((project as any)?.jiraProjectId);
            if (jiraProjectId && jiraProjectId !== '0') {
                linkedJiraIds.add(jiraProjectId);
                const mappedKey = jiraKeyById.get(jiraProjectId);
                if (mappedKey) {
                    linkedJiraKeys.add(mappedKey);
                }
            }

            // Also check jiraProjectKey field
            const rawStoredKey = this.normalizeString((project as any)?.jiraProjectKey);
            if (!rawStoredKey || rawStoredKey === '0') {
                return;
            }

            // If it's a numeric value, treat it as an ID
            if (/^\d+$/.test(rawStoredKey)) {
                linkedJiraIds.add(rawStoredKey);
                const mappedByNumericKey = jiraKeyById.get(rawStoredKey);
                if (mappedByNumericKey) {
                    linkedJiraKeys.add(mappedByNumericKey);
                }
            } else {
                // It's an alphanumeric key
                const normalizedKey = this.normalizeJiraKey(rawStoredKey);
                linkedJiraKeys.add(normalizedKey);
                const mappedId = jiraIdByKey.get(normalizedKey);
                if (mappedId) {
                    linkedJiraIds.add(mappedId);
                }
            }
        });

        // Filter Jira projects that are NOT yet linked
        this.jiraMissingProjects = this.jiraProjects
            .map((project) => ({
                projectName: (project?.projectName || '').toString(),
                projectKey: this.normalizeJiraKey(project?.projectKey),
                projectId: project?.projectId,
            }))
            .filter((project) => !!project.projectKey)
            .filter((project) => {
                const id = this.normalizeString(project.projectId);
                const key = project.projectKey;
                // If either the ID or the key is already linked, skip it
                if (linkedJiraIds.has(id) || linkedJiraKeys.has(key)) {
                    return false;
                }
                return true;
            })
            .filter((project) => {
                // Also check by title as a fallback
                const normalizedTitle = this.normalizeProjectTitle(project.projectName);
                return !normalizedTitle || !existingTitles.has(normalizedTitle);
            });
    }

    private refreshFinancialTabCostsForProjects(projects: Project[]): void {
        if (!projects?.length) {
            return;
        }

        const employeesWithBudgets = this.store.selectSnapshot(BudgetState.EmployeesWithBudgets);
        if (!employeesWithBudgets) {
            this.subscriptions.add(this.store.dispatch(new FetchEmployeesWithBudgets()).subscribe(() => this.refreshFinancialTabCostsForProjects(projects)));
            return;
        }

        projects
            .filter((project) => this.showContractBudgetProgress(project) && !!project?.uuid)
            .forEach((project) => {
                const projectId = project.uuid;

                this.subscriptions.add(
                    forkJoin({
                        linkedFilesToObject: this.globalFilesService.getAllFilesLinkedToObject(projectId).pipe(catchError(() => of(null))),
                        timesheets: this.financialService.getTimesheetsForProject(projectId, this.currentYear, null).pipe(catchError(() => of([]))),
                    }).subscribe(({ linkedFilesToObject, timesheets }) => {
                        const purchaseCost = this.calculatePurchaseCostsForYear(linkedFilesToObject?.linkedFiles || []);
                        const laborCost = this.calculateLaborCostsForYear(timesheets || [], projectId);
                        const totalCost = purchaseCost + laborCost;

                        this.financialTabCostByProjectId.set(projectId, totalCost);
                        project.currentYearTotalCostAmount = totalCost;

                        const budget = Number(project?.currentYearContractBudget ?? 0);
                        if (Number.isFinite(budget) && budget > 0) {
                            project.currentYearCostBudgetUsagePercentage = (totalCost / budget) * 100;
                        }

                        if (this.dataSource?.data?.length) {
                            this.dataSource.data = [...this.dataSource.data];
                        }
                    }),
                );
            });
    }

    private refreshIncomingInvoiceAmounts(projects: Project[]): void {
        if (!projects?.length) {
            return;
        }

        projects
            .filter((project) => !!project?.uuid)
            .forEach((project) => {
                const projectId = project.uuid;
                this.subscriptions.add(
                    this.globalFilesService
                        .getAllFilesLinkedToObject(projectId)
                        .pipe(catchError(() => of(null)))
                        .subscribe((payload) => {
                            const linkedFiles = payload?.linkedFiles || [];
                            const incomingAmount = this.sumIncomingLinkedInvoices(linkedFiles);
                            const outgoingAmount = this.sumOutgoingLinkedInvoices(linkedFiles);
                            this.incomingInvoicesAmountByProjectId.set(projectId, incomingAmount);
                            this.outgoingInvoicesAmountByProjectId.set(projectId, outgoingAmount);
                            this.loadInvoiceBudgetAmount(projectId, linkedFiles);
                            this.triggerTableRefresh();
                        }),
                );
            });
    }

    private loadInvoiceBudgetAmount(projectId: string, linkedFiles: LinkedFileToObject[]): void {
        this.subscriptions.add(
            this.globalFilesService
                .getAllObjectsLinked()
                .pipe(catchError(() => of([])))
                .subscribe((allLinkedObjects: LinkedObjectsToFileWithId[]) => {
                    const linkedByFileId = (allLinkedObjects || []).reduce((acc: Record<string, LinkedObjectsToFileWithId>, item) => {
                        const key = (item?.yukiId || '').toString();
                        if (key) {
                            acc[key] = item;
                        }
                        return acc;
                    }, {});

                    let allocatedBudgetAmount = 0;
                    (linkedFiles || [])
                        .filter((file) => this.isOutgoingInvoice(file))
                        .forEach((file) => {
                            const fileId = (file?.fileId || '').toString();
                            if (!fileId) return;

                            const linkedForFile = linkedByFileId[fileId];
                            const projectLink = (linkedForFile?.linkedObjects || []).find((link) => link?.objectId === projectId);
                            (projectLink?.childInvoiceLinks || []).forEach((child: ChildInvoiceLink) => {
                                const objectType = (child?.objectType || '').toLowerCase();
                                if (objectType !== 'employee' && objectType !== 'budget') return;

                                const amount = Math.abs(Number(child?.amount || 0));
                                if (!Number.isFinite(amount) || amount <= 0) return;
                                allocatedBudgetAmount += amount;
                            });
                        });

                    this.invoiceBudgetByProjectId.set(projectId, allocatedBudgetAmount);
                    this.triggerTableRefresh();
                }),
        );
    }

    private isOutgoingInvoice(file: LinkedFileToObject): boolean {
        const type = (file?.invoiceType || '').toLowerCase();
        const description = (file?.description || '').toLowerCase();
        const title = (file?.title || '').toLowerCase();
        const isArchivedOrDeleted =
            type.includes('archived') ||
            type.includes('deleted') ||
            description.includes('archived') ||
            description.includes('deleted') ||
            title.includes('archived') ||
            title.includes('deleted');

        return file?.folderId === 2 && !isArchivedOrDeleted;
    }

    private sumIncomingLinkedInvoices(files: LinkedFileToObject[]): number {
        return (files || [])
            .filter((file) => {
                const type = (file?.invoiceType || '').toLowerCase();
                const description = (file?.description || '').toLowerCase();
                const title = (file?.title || '').toLowerCase();
                const isArchivedOrDeleted =
                    type.includes('archived') ||
                    type.includes('deleted') ||
                    description.includes('archived') ||
                    description.includes('deleted') ||
                    title.includes('archived') ||
                    title.includes('deleted');

                return file?.folderId === 1 && !isArchivedOrDeleted;
            })
            .reduce((sum, file) => sum + Math.abs(Number(file?.price || 0)), 0);
    }

    private sumOutgoingLinkedInvoices(files: LinkedFileToObject[]): number {
        return (files || [])
            .filter((file) => {
                const type = (file?.invoiceType || '').toLowerCase();
                const description = (file?.description || '').toLowerCase();
                const title = (file?.title || '').toLowerCase();
                const isArchivedOrDeleted =
                    type.includes('archived') ||
                    type.includes('deleted') ||
                    description.includes('archived') ||
                    description.includes('deleted') ||
                    title.includes('archived') ||
                    title.includes('deleted');

                return file?.folderId === 2 && !isArchivedOrDeleted;
            })
            .reduce((sum, file) => sum + Math.abs(Number(file?.price || 0)), 0);
    }

    private refreshAccountBudgetStatusesForProjects(projects: Project[]): void {
        if (!projects?.length) {
            return;
        }

        projects
            .filter((project) => !!project?.uuid)
            .forEach((project) => {
                const projectId = project.uuid;
                if (this.accountBudgetStatusesLoadedProjectIds.has(projectId) || this.accountBudgetStatusesLoadingByProjectId.get(projectId) === true) {
                    return;
                }

                this.accountBudgetStatusesLoadingByProjectId.set(projectId, true);
                this.triggerTableRefresh();
                this.subscriptions.add(
                    this.projectService
                        .getProjectAccounts(projectId)
                        .pipe(catchError(() => of([])))
                        .subscribe((accounts: any[]) => {
                            const budgetedAccounts = (accounts || []).filter((account) => {
                                const budget = Number(account?.budget || 0);
                                if (budget <= 0) {
                                    return false;
                                }

                                return !this.isOverviewContractExpired(account?.effectiveFrom, account?.effectiveTo);
                            });
                            if (!budgetedAccounts.length) {
                                this.accountBudgetStatusesByProjectId.set(projectId, []);
                                this.accountBudgetStatusesLoadingByProjectId.set(projectId, false);
                                this.accountBudgetStatusesLoadedProjectIds.add(projectId);
                                if (this.dataSource?.data?.length) {
                                    this.dataSource.data = [...this.dataSource.data];
                                }
                                return;
                            }

                            const statuses: ContractBudgetStatus[] = budgetedAccounts
                                .map((account) => {
                                    const budget = Number(account?.budget || 0);
                                    const amount = Number(account?.yearlyActualCost || 0);
                                    const usagePercentage = budget > 0 ? (amount / budget) * 100 : 0;
                                    return {
                                        id: account?.uuid,
                                        sourceType: 'PROJECT_ACCOUNT',
                                        label: this.getOverviewAccountLabel(account),
                                        budget,
                                        amount,
                                        usagePercentage,
                                    } as ContractBudgetStatus;
                                })
                                .filter((status) => !!status && Number(status?.budget || 0) > 0)
                                .sort((a, b) => (a?.label || '').localeCompare(b?.label || '', undefined, { sensitivity: 'base' }));

                            this.accountBudgetStatusesByProjectId.set(projectId, statuses);
                            this.accountBudgetStatusesLoadingByProjectId.set(projectId, false);
                            this.accountBudgetStatusesLoadedProjectIds.add(projectId);
                            if (this.dataSource?.data?.length) {
                                this.dataSource.data = [...this.dataSource.data];
                            }
                        }),
                );
            });
    }

    private getOverviewAccountLabel(account: any): string {
        const code = (account?.accountCode || account?.jiraAccountId || account?.accountName || '').toString().trim();
        const title = (account?.accountTitle || account?.accountName || '').toString().trim();
        const employeeName = this.getEmployeeDisplayName(account?.employeeId);

        if (code && title && title !== code) {
            return `${code} (${title})`;
        }

        const isCodeOnlyNumeric = !!code && /^\d+$/.test(code);
        if (isCodeOnlyNumeric && employeeName) {
            return `${employeeName} (${code})`;
        }

        return employeeName || code || title || 'Project account';
    }

    private getEmployeeDisplayName(employeeId?: string): string {
        if (!employeeId) {
            return '';
        }

        const employee = this.employeesById?.[employeeId];
        if (!employee) {
            return '';
        }

        const fullName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
        return fullName || employeeId;
    }

    getBusinessDeveloperDisplay(project: Project): string {
        const explicitName = (project?.businessDeveloperName || '').trim();
        if (explicitName) {
            return explicitName;
        }

        const fullName = `${project?.businessDeveloperFirstname || ''} ${project?.businessDeveloperLastname || ''}`.trim();
        if (fullName) {
            return fullName;
        }

        return this.getEmployeeDisplayName(project?.businessDeveloperUuid) || '-';
    }

    private parseOverviewIsoDate(value?: string | null): Date | null {
        if (!value) {
            return null;
        }

        const isoPart = value.trim().split('T')[0];
        const parts = isoPart.split('-').map((part) => Number(part));
        if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
            return null;
        }

        const parsed = new Date(parts[0], parts[1] - 1, parts[2]);
        if (Number.isNaN(parsed.getTime())) {
            return null;
        }
        return parsed;
    }

    private isOverviewContractExpired(effectiveFrom?: string | null, effectiveTo?: string | null): boolean {
        const today = this.getOverviewDateWithoutTime(new Date());

        let fromDate = this.parseOverviewIsoDate(effectiveFrom);
        let toDate = this.parseOverviewIsoDate(effectiveTo);

        if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
            const swappedFrom = toDate;
            toDate = fromDate;
            fromDate = swappedFrom;
        }

        return !!toDate && this.getOverviewDateWithoutTime(toDate).getTime() < today.getTime();
    }

    private getOverviewDateWithoutTime(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    private calculatePurchaseCostsForYear(files: LinkedFileToObject[]): number {
        const startDate = new Date(this.currentYear, 0, 1);
        const endDate = new Date(this.currentYear, 11, 31, 23, 59, 59);

        return (files || [])
            .filter((file) => {
                const type = (file?.invoiceType || '').toLowerCase();
                const description = (file?.description || '').toLowerCase();
                const title = (file?.title || '').toLowerCase();
                const isArchivedOrDeleted =
                    type.includes('archived') ||
                    type.includes('deleted') ||
                    description.includes('archived') ||
                    description.includes('deleted') ||
                    title.includes('archived') ||
                    title.includes('deleted');

                return (
                    file?.fileType?.toLowerCase() === 'yukidocument' &&
                    file?.folderId === 1 &&
                    !!file?.invoiceDate &&
                    new Date(file.invoiceDate) < endDate &&
                    new Date(file.invoiceDate) > startDate &&
                    !isArchivedOrDeleted
                );
            })
            .reduce((sum, file) => sum + Math.abs(Number(file?.price || 0)), 0);
    }

    private calculateLaborCostsForYear(timesheets: any[], projectId: string): number {
        const employeesWithBudgets = this.store.selectSnapshot(BudgetState.EmployeesWithBudgets) || [];
        const perEmployeeMonthHours: Record<string, Record<number, number>> = {};

        (timesheets || []).forEach((timesheet) => {
            const employeeId = timesheet?.employee?.uuid;
            if (!employeeId) {
                return;
            }

            const expirationDate = new Date(timesheet?.expirationDate);
            if (expirationDate.getFullYear() !== this.currentYear) {
                return;
            }

            const month = expirationDate.getMonth();
            if (!perEmployeeMonthHours[employeeId]) {
                perEmployeeMonthHours[employeeId] = {};
            }
            if (!perEmployeeMonthHours[employeeId][month]) {
                perEmployeeMonthHours[employeeId][month] = 0;
            }

            (timesheet?.timeslots || []).forEach((slot) => {
                if (slot?.project?.uuid === projectId) {
                    perEmployeeMonthHours[employeeId][month] += Number(slot?.minutes || 0) / 60;
                }
            });
        });

        return Object.keys(perEmployeeMonthHours).reduce((totalByProject, employeeId) => {
            const employee = employeesWithBudgets.find((item: any) => item.uuid === employeeId);
            if (!employee) {
                return totalByProject;
            }

            const budgets = employee.budgets || [];
            const workPercentage = employee.workPercentage;

            const totalByEmployee = Object.keys(perEmployeeMonthHours[employeeId]).reduce((totalByMonth, monthKey) => {
                const month = Number(monthKey);
                const hoursWorked = Number(perEmployeeMonthHours[employeeId][month] || 0);
                if (hoursWorked <= 0) {
                    return totalByMonth;
                }

                const targetDate = new Date(this.currentYear, month, 15);
                const monthlyTotal = this.budgetCalc.computeMonthlyBudgetTotal(budgets, targetDate);
                const { projectCost } = this.budgetCalc.computeProjectCost(monthlyTotal, hoursWorked, workPercentage);
                return totalByMonth + Number(projectCost || 0);
            }, 0);

            return totalByProject + totalByEmployee;
        }, 0);
    }

    private normalizeString(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }
        return String(value).trim();
    }

    private normalizeJiraKey(value: unknown): string {
        return this.normalizeString(value).toUpperCase();
    }

    private normalizeProjectTitle(value: unknown): string {
        return this.normalizeString(value).toLowerCase();
    }

    private buildJiraKeyLookupMap(): void {
        this.jiraKeyByProjectId.clear();
        (this.jiraProjects || []).forEach((project) => {
            const id = this.normalizeString(project?.projectId);
            const key = this.normalizeJiraKey(project?.projectKey);
            if (id && key) {
                this.jiraKeyByProjectId.set(id, key);
            }
        });
    }

    getJiraKeyForProject(project: Project): string {
        if (!project) {
            return '';
        }
        // Try to resolve via jiraProjectId (numeric ID stored in database)
        const jiraProjectId = this.normalizeString((project as any)?.jiraProjectId);
        if (jiraProjectId && this.jiraKeyByProjectId.has(jiraProjectId)) {
            return this.jiraKeyByProjectId.get(jiraProjectId);
        }
        // Fallback: check if jiraProjectKey is already an alphanumeric key
        const storedKey = this.normalizeString(project?.jiraProjectKey);
        if (storedKey && !/^\d+$/.test(storedKey)) {
            return this.normalizeJiraKey(storedKey);
        }
        // If jiraProjectKey is numeric, try to map it
        if (storedKey && /^\d+$/.test(storedKey) && this.jiraKeyByProjectId.has(storedKey)) {
            return this.jiraKeyByProjectId.get(storedKey);
        }
        return '';
    }

    private loadCoManageContactLabels(): void {
        this.subscriptions.add(
            this.comanageService.getContacts().subscribe(
                (contacts: any[]) => {
                    const map = new Map<string, string>();
                    (contacts || []).forEach((contact: any) => {
                        const id = this.getCoManageContactId(contact);
                        if (!id) {
                            return;
                        }
                        const name = (contact?.name || '').toString().trim();
                        map.set(id, name || id);
                    });
                    this.coManageContactLabelById = map;
                },
                () => {
                    this.coManageContactLabelById = new Map<string, string>();
                },
            ),
        );
    }

    private getCoManageContactId(contact: any): string {
        const raw = contact?.comanageOptionId ?? contact?.number ?? contact?.supplier_number ?? contact?.customer_number ?? contact?.id ?? '';
        return raw != null ? String(raw).trim() : '';
    }

    getCoManageContactDisplay(contactId: unknown): string {
        const id = this.normalizeString(contactId);
        if (!id) {
            return '';
        }

        return this.coManageContactLabelById.get(id) || id;
    }

    private isFreelancerEmployee(employeeId?: string): boolean {
        if (!employeeId) {
            return false;
        }
        const employeeType = (this.employeesById?.[employeeId]?.type || '').toString().toUpperCase();
        return employeeType === 'FREELANCE' || employeeType === 'FREELANCER';
    }

    private isFreelancerContractStatus(project: Project, status: ContractBudgetStatus): boolean {
        if ((status?.sourceType || '').toUpperCase() !== 'CONTRACT') {
            return false;
        }

        if (status?.freelancer === true) {
            return true;
        }

        const statusId = (status?.id || '').toString();
        const defaultLinks = project?.defaultLinkedEmployees || [];

        const contractLinkById = defaultLinks.find((link) => String(link?.uuid || '') === statusId);
        if (contractLinkById) {
            return this.isFreelancerEmployee(contractLinkById?.employeeId);
        }

        const normalizedLabel = (status?.label || '').toString().trim().toLowerCase();
        if (!normalizedLabel) {
            return false;
        }

        const matchingByName = defaultLinks.find((link) => {
            if (!this.isFreelancerEmployee(link?.employeeId)) {
                return false;
            }
            const employee = this.employeesById?.[link?.employeeId || ''];
            const fullName = `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim().toLowerCase();
            return !!fullName && (normalizedLabel === fullName || normalizedLabel.includes(fullName) || fullName.includes(normalizedLabel));
        });
        if (matchingByName) {
            return true;
        }

        const freelancerLinks = defaultLinks.filter((link) => this.isFreelancerEmployee(link?.employeeId));
        if (freelancerLinks.length === 1 && defaultLinks.length === 1) {
            return true;
        }

        return false;
    }

    getBudgetByInvoiceAmount(project: Project): number {
        const projectId = project?.uuid || '';
        const salesInvoicesAmount = Number(project?.currentYearInvoiceBudgetAmount ?? this.invoiceBudgetByProjectId.get(projectId) ?? 0);
        if (!Number.isFinite(salesInvoicesAmount) || salesInvoicesAmount < 0) {
            return 0;
        }
        return salesInvoicesAmount;
    }

    getBudgetByInvoiceUsagePercentage(project: Project): number {
        const budget = this.getResolvedContractBudget(project);
        if (!Number.isFinite(budget) || budget <= 0) {
            return 0;
        }

        const amount = this.getBudgetByInvoiceAmount(project);
        const usage = (amount / budget) * 100;
        if (!Number.isFinite(usage) || usage < 0) {
            return 0;
        }

        return usage;
    }

    getBudgetByInvoiceProgressBarValue(project: Project): number {
        const usage = this.getBudgetByInvoiceUsagePercentage(project);
        return Math.max(0, Math.min(100, usage));
    }

    getBudgetByInvoiceTooltip(project: Project): string {
        const amount = this.getBudgetByInvoiceAmount(project);
        const budget = this.getResolvedContractBudget(project);
        const usage = this.getBudgetByInvoiceUsagePercentage(project);

        return [
            `Berekening: (verkoopfacturen / totaal budget) x 100`,
            `Verkoopfacturen (gelinkt aan contract/budget): € ${amount.toFixed(2)}`,
            `Totaal budget: € ${budget.toFixed(2)}`,
            `Resultaat: ${usage.toFixed(2)}%`,
        ].join('\n');
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}

export enum ProjectListType {
    ONGOING,
    FINISHED,
    ARCHIVED,
    HAS_MAINTENANCE_CONTRACT,
}
